// Helperfunctions for map page

import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

import '../../style/map.scss';
import { fetchTAFV2, fetchMETAR, getNOTAMListFromAllData } from '../helperfunctions';
import { formatLongLat, getMainRouteCoordinates } from './mapHelperFunctions';

// Draws lines between waypoints of the main route with the color responding to a waypoints shearing rate(turbulence)
// Also adds a gradient when the turbulence severity increases/decreases in level
// Used for turbulence filter
// Also adds waypoints of main route, along with dep and arr airports
export function addTurbulenceRouteWaypoints(
  map,
  data,
  markerList,
  handleOpenAirportModal,
  mapLayers,
  mapSources
) {
  if (!data) return;
  // i represents a waypoint index
  for (let i = 0; i < data.WP_List[0].WayPoint.length - 1; i++) {
    // get turbuluence number and coordinates for current waypoint
    const currentWaypointTurbNumber =
      data.WP_List[0].WayPoint[i].sSegmentShearRate[0][0];
    const currentWaypointFormattedLong = formatLongLat(
      data.WP_List[0].WayPoint[i].nLon
    );
    const currentWaypointFormattedLat = formatLongLat(
      data.WP_List[0].WayPoint[i].nLat
    );
    // get turbuluence number and coordinates for next waypoint
    const nextWaypointTurbNumber =
      data.WP_List[0].WayPoint[i + 1].sSegmentShearRate[0][0];
    const nextWaypointFormattedLong = formatLongLat(
      data.WP_List[0].WayPoint[i + 1].nLon
    );
    const nextWaypointFormattedLat = formatLongLat(
      data.WP_List[0].WayPoint[i + 1].nLat
    );

    const sourceId = 'turbLine' + i;
    if (!map.current.getSource(sourceId)) {
      map.current.addSource(sourceId, {
        type: 'geojson',
        lineMetrics: true,
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [
              [currentWaypointFormattedLong, currentWaypointFormattedLat],
              [nextWaypointFormattedLong, nextWaypointFormattedLat],
            ],
          },
        },
      });
      if (mapSources) mapSources.add(sourceId);
    }

    const layerId = 'turbLine' + i;
    if (!map.current.getLayer(layerId)) {
      map.current.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': getColorFromTurb(currentWaypointTurbNumber),
          'line-width': 5,
          'line-gradient': [
            'interpolate',
            ['linear'],
            ['line-progress'],
            0,
            getColorFromTurb(currentWaypointTurbNumber),
            0.5,
            getColorFromTurb(nextWaypointTurbNumber),
            1,
            getColorFromTurb(nextWaypointTurbNumber),
          ],
        },
      });
      if (mapLayers) mapLayers.add(layerId);
    }
  }

  const mainRouteCoordinates = getMainRouteCoordinates(data);
  const notamList = getNOTAMListFromAllData(data);

  // departure airport
  const depWaypointId = data.WP_List[0].WayPoint[0].sWaypointId;
  const depAirportName = data.WP_List[0].WayPoint[0].sWaypointLongName;
  const depAirportICAO = data.sDepICAO;
  let depAirportTAF;
  let depAirportMETAR;

  // fetching taf data (using V2 endpoint with JSON format)
  fetchTAFV2(depAirportICAO)
    .then((res) => res.json())
    .then(
      (res) =>
      (depAirportTAF = res?.[0]?.rawTAF
        ? res[0].rawTAF
        : 'TAF data not found')
    );

  // fetching metar data
  fetchMETAR(depAirportICAO)
    .then((res) => res.json())
    .then((res) => (depAirportMETAR = res?.[0]?.METAR?.[0]));

  const depWaypointContainer = document.createElement('div');
  depWaypointContainer.className = 'waypoint-container';

  const depMarker = document.createElement('div');
  depMarker.className = 'dep-arr-waypoint-img';

  const depIdElement = document.createElement('span');
  depIdElement.className = 'dep-arr-waypoint-text';
  const depIdText = document.createTextNode(depWaypointId);
  depIdElement.appendChild(depIdText);

  depWaypointContainer.appendChild(depMarker);
  depWaypointContainer.appendChild(depIdElement);

  const depWaypoint = new mapboxgl.Marker(depWaypointContainer)
    .setLngLat(mainRouteCoordinates[0])
    .addTo(map.current);
  depMarker.onclick = () =>
    handleOpenAirportModal(
      depAirportName,
      data.sDepIATA,
      depAirportMETAR,
      depAirportTAF,
      depAirportICAO,
      notamList
    );

  markerList.push(depWaypoint);

  // waypoints between dep and arr airport
  for (let i = 1; i < data.WP_List[0].WayPoint.length - 1; i++) {
    const waypointId = data.WP_List[0].WayPoint[i].sWaypointId;

    const pointElement = document.createElement('div');
    pointElement.className = 'waypoint-container z-indexZero';

    const pointImg = document.createElement('div');
    pointImg.className = 'waypoint-img';

    const pointId = document.createElement('span');
    pointId.className = 'route-waypoint-text';
    const pointIdText = document.createTextNode(waypointId);
    pointId.appendChild(pointIdText);

    pointElement.appendChild(pointImg);
    pointElement.appendChild(pointId);

    const marker = new mapboxgl.Marker(pointElement)
      .setLngLat(mainRouteCoordinates[i])
      .addTo(map.current);
    markerList.push(marker);
  }

  // arr airport
  const arrWaypointIndex = data.WP_List[0].WayPoint.length - 1;
  const arrWaypointId = data.WP_List[0].WayPoint[arrWaypointIndex].sWaypointId;
  const arrAirportName =
    data.WP_List[0].WayPoint[arrWaypointIndex].sWaypointLongName;
  const arrAirportICAO = data.sDestICAO;
  let arrAirportTAF;
  let arrAirportMETAR;

  // fetching taf data (using V2 endpoint with JSON format)
  fetchTAFV2(arrAirportICAO)
    .then((res) => res.json())
    .then(
      (res) =>
      (arrAirportTAF = res?.[0]?.rawTAF
        ? res[0].rawTAF
        : 'TAF data not found')
    );

  // fetching metar data
  fetchMETAR(arrAirportICAO)
    .then((res) => res.json())
    .then((res) => (arrAirportMETAR = res?.[0]?.METAR?.[0]));

  const arrWaypointContainer = document.createElement('div');
  arrWaypointContainer.className = 'waypoint-container';

  const arrMarker = document.createElement('div');
  arrMarker.className = 'dep-arr-waypoint-img';

  const arrIdElement = document.createElement('span');
  arrIdElement.className = 'dep-arr-waypoint-text';
  const arrIdText = document.createTextNode(arrWaypointId);
  arrIdElement.appendChild(arrIdText);

  arrWaypointContainer.appendChild(arrMarker);
  arrWaypointContainer.appendChild(arrIdElement);

  const arrWaypointMarker = new mapboxgl.Marker(arrWaypointContainer)
    .setLngLat(mainRouteCoordinates[mainRouteCoordinates.length - 1])
    .addTo(map.current);
  arrMarker.onclick = () =>
    handleOpenAirportModal(
      arrAirportName,
      data.sDestIATA,
      arrAirportMETAR,
      arrAirportTAF,
      arrAirportICAO,
      notamList
    );

  markerList.push(arrWaypointMarker);
}

// 0-2 = low turb (green)
// 3-5 = medium turb (yellow)
// 6-7 = high turb (red)
// returns a color from by inputting a specified turbulence number
export function getColorFromTurb(turbNumber) {
  if (turbNumber === undefined || parseInt(turbNumber) < 3) {
    return 'green';
  }
  if (parseInt(turbNumber) > 2 && parseInt(turbNumber) < 6) {
    return 'yellow';
  }
  if (parseInt(turbNumber) > 5) {
    return 'red';
  }

  return 'black';
}
